import { Component, DoCheck, inject, input, viewChildren, effect } from '@angular/core';
import { ExportCsvService } from 'src/app/services/export-csv.service';
import { CsvModes } from 'src/app/core/csv-modes.class';
import { RouterModule } from '@angular/router';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { DealHierarchy } from 'src/app/models/classes/deal-hierarchy.class';
import { TableLogicService } from 'src/app/services/table-logic.service';
import { ActivityTrackingService } from 'src/app/services/activity-tracking.service';
import { CommonModule } from '@angular/common';
import { MatTooltipModule } from '@angular/material/tooltip';
import { CurrencyFormatPipe } from 'src/app/pipes/currency-format.pipe';
import { TvpiValueFormatPipe } from 'src/app/pipes/tvpi-value-format.pipe';
import { IrrValueFormatPipe } from 'src/app/pipes/irr-value-format.pipe';
import { MatIconModule } from '@angular/material/icon';
import { TourMatMenu } from 'ngx-ui-tour-md-menu';
import { MatSortModule } from '@angular/material/sort';
import { AggregatedPortfolioSeries } from 'src/app/models/classes/aggregated-portfolio-series.class';
import { PortfolioSeries } from 'src/app/models/classes/portfolio-series.class';
import { InvestmentTypeHierarchy } from 'src/app/models/classes/investment-type-hierarchy.class';
import { Portfolio } from 'src/app/models/classes/portfolio.class';
import { SeriesTableConfigService } from 'src/app/services/series-table-config.service';

@Component({
    selector: 'app-series-qroll-table-real-estate',
    imports: [
        CommonModule,
        MatTooltipModule,
        CurrencyFormatPipe,
        TvpiValueFormatPipe,
        MatTableModule,
        IrrValueFormatPipe,
        RouterModule,
        MatIconModule,
        TourMatMenu,
        MatSortModule
    ],
    providers: [TvpiValueFormatPipe, IrrValueFormatPipe],
    templateUrl: './series-qroll-table-real-estate.component.html',
    styleUrls: ['../../series-qroll-table/series-qroll-table.component.scss']
})
export class SeriesQRollTableRealEstateComponent implements DoCheck {
  private tableLogicService = inject(TableLogicService);
  private exportCsvService = inject(ExportCsvService);
  private tvpiValueFormatPipe = inject(TvpiValueFormatPipe);
  private irrValueFormatPipe = inject(IrrValueFormatPipe);
  activityTracking = inject(ActivityTrackingService);
  private seriesTableConfig = inject(SeriesTableConfigService);

  readonly iconSet = input<Record<string, string> | undefined>(undefined);
  readonly investmentTypes = input<InvestmentTypeHierarchy[]>();
  readonly portfolioSummary = input<Portfolio>();
  readonly summary = input<AggregatedPortfolioSeries | PortfolioSeries>();
  readonly portfolioName = input<string>('');
  readonly seriesName = input<string>('');
  readonly investorId = input<number | undefined>(undefined);
  readonly seriesId = input<number | undefined>(undefined);
  readonly reportingPeriod = input<string | undefined>(undefined);
  readonly isAggregatedSeries = input<boolean>(false);
  deals: DealHierarchy[] = [];
  sortingInitialized = false;
  displayedColumns: string[] = [];
  private static readonly multipleRowHeaders = ['name', 'realizationType', 'priorNAV', 'nav'];
  headerColumns: string[] = [];
  innerDisplayedColumns: string[] = [];
  displayShadowOnQRTable = false;

  constructor() {
    effect(() => {
      const cfg = this.seriesTableConfig.config();
      this.displayedColumns = cfg.qRoll.columns;
      this.headerColumns = cfg.qRoll.columns.filter(
        (column) => !SeriesQRollTableRealEstateComponent.multipleRowHeaders.includes(column),
      );
      this.innerDisplayedColumns = cfg.qRoll.innerColumns;
    });
  }
  sorts = viewChildren(MatSort);
  dataSource = new MatTableDataSource<DealHierarchy>([]);

  private readonly rebuildQRollData = effect(() => {
    if (this.investmentTypes()) {
      this.formatQRollTableValues();
      this.sortingInitialized = false;
    }
  });

  ngDoCheck() {
    this.initializeSorting();
  }

  initializeSorting() {
    if (!this.sortingInitialized && this.sorts().length > 0) {
      this.dataSource.sort = this.sorts()[0];
      this.sortingInitialized = true;
    }
  }

  formatQRollTableValues() {
    this.deals = this.tableLogicService.sortDealsByCommitmentDate(
      (this.investmentTypes() ?? []).flatMap((investmentType) => investmentType.deals),
    );
    this.deals.forEach(deal => {
      deal.showHoldings = this.tableLogicService.checkDealToHoldingMapping(deal.holdings);
      deal.disableDrillThrough = this.tableLogicService.isDealDrillThroughDisabled(deal);
    });
    this.dataSource = new MatTableDataSource(this.deals);
  }

  exportQRollDataToCsv(event: MouseEvent) {
    const seriesName = this.seriesName() ?? '';
    this.activityTracking.exportToExcelClick('series qroll table', seriesName);
    (event.target as HTMLButtonElement).disabled = true;
    const qrollForwardDealsArray = [], qrollForwardHoldingsArray = [];
    const filename = this.exportCsvService.getExportFilename(CsvModes.quarterlyRollForwardDeals, this.reportingPeriod() ?? '', this.portfolioName() ?? '', seriesName);
    const headerForQRollForwardDeals = this.omitInvestmentType(this.exportCsvService.getCsvHeader(CsvModes.quarterlyRollForwardDeals) as unknown as Record<string, string>);
    const headerForQRollForwardHoldings = this.omitInvestmentType(this.exportCsvService.getCsvHeader(CsvModes.quarterlyRollForwardHoldings) as unknown as Record<string, string>);
    qrollForwardDealsArray.push(headerForQRollForwardDeals);
    qrollForwardHoldingsArray.push(headerForQRollForwardHoldings);

    const sortedDeals = this.exportCsvService.sortArrayAscending(this.deals);
    sortedDeals.forEach((deal: DealHierarchy) => {
      const dealData: Record<string, unknown> = {};
      dealData['dealName'] = deal.name;
      dealData['invStatus'] = deal.realizationType;
      dealData['dealPriorNav'] = deal.priorNAV;
      dealData['dealQoqContributions'] = deal.qoQContributions;
      dealData['dealQoqDistributions'] = deal.qoQDistributions;
      dealData['dealGainLoss'] = deal.qoQGainLoss;
      dealData['dealCurrentNav'] = deal.nav;
      dealData['dealQoqTvpiChange'] = this.tvpiValueFormatPipe.transform(deal.qoQTvpiChange);
      dealData['dealQoqIrrChange'] = this.irrValueFormatPipe.transform(deal.qoQIrrChange);
      qrollForwardDealsArray.push(dealData);
      const sortedHoldings = this.exportCsvService.sortArrayAscending(deal.holdings);
      sortedHoldings.forEach((holding: DealHierarchy['holdings'][number]) => {
        const data: Record<string, unknown> = {};
        data['dealName'] = deal.name;
        data['holdingName'] = holding.name;
        data['invStatus'] = deal.realizationType;
        data['holdingPriorNav'] = holding.priorNAV;
        data['holdingQoqContributions'] = holding.qoQContributions;
        data['holdingQoqDistributions'] = holding.qoQDistributions;
        data['holdingGainLoss'] = holding.qoQGainLoss;
        data['holdingCurrentNav'] = holding.nav;
        qrollForwardHoldingsArray.push(data);
      })
    })
    const worksheets = [{ name: 'Q Roll Forward - Deals', csvArray: qrollForwardDealsArray }, { name: 'Q Roll Forward - Holdings', csvArray: qrollForwardHoldingsArray }]
    this.exportCsvService.exportToExcelFile(filename, worksheets);
    (event.target as HTMLButtonElement).disabled = false;
  }

  /** CS-4970: drops Investment Type — Real Estate no longer groups or tracks by it. */
  private omitInvestmentType(header: Record<string, string>): Record<string, string> {
    const updatedHeader = { ...header };
    delete updatedHeader['invType'];
    return updatedHeader;
  }

  scrollQRTableHandler(event: Event) {
    this.displayShadowOnQRTable = (event.target as HTMLElement).scrollLeft > 0;
  }
}
